import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increase timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Add these for better error handling
  validateStatus: function (status) {
    return status < 500; // Resolve only if the status code is less than 500
  }
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('Making request:', config.method?.toUpperCase(), config.baseURL + config.url);
    console.log('Request config:', config);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('Response received:', response.status, response.statusText);
    return response;
  },
  (error) => {
    console.error('Response error:', error);
    return Promise.reject(error);
  }
);

// Newsroom API Service (Collection Type)
export const newsroomService = {
  // Get all newsroom posts (Collection Type - returns array)
  async getNewsroomPosts(params = {}) {
    // Try native fetch first as it seems more reliable
    try {
      const queryParams = new URLSearchParams({
        'populate[images][populate]': 'images',
        'pagination[limit]': 1000, // Set high limit to ensure all news are fetched
        ...params
      });
      
      const fetchResponse = await fetch(`${API_BASE_URL}/newsrooms?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }
      
      const data = await fetchResponse.json();
      
      // Get the data array
      let newsData = data.data || [];
      
      // Custom sorting: items with published_date first (newest to oldest), 
      // then items without published_date last
      newsData = newsData.sort((a, b) => {
        const aDate = a.published_date || a.attributes?.published_date;
        const bDate = b.published_date || b.attributes?.published_date;
        
        if (aDate && bDate) {
          return new Date(bDate) - new Date(aDate);
        }
        
        if (aDate && !bDate) {
          return -1;
        }
        
        if (!aDate && bDate) {
          return 1;
        }
        
        return 0;
      });
      
      return {
        data: newsData,
        meta: data.meta,
        success: true
      };
      
    } catch (error) {
      console.error('Error fetching newsroom data:', error);
      console.error('Error message:', error.message);
      
      return {
        data: [],
        success: false,
        error: error.message || 'Failed to fetch newsroom data',
        meta: { pagination: { total: 0 } }
      };
    }
  },

  // Get single newsroom post by ID (can be news_id or strapi_id)
  async getNewsroomPostById(id) {
    try {
      // First try to get by Strapi ID directly using fetch
      // const response = await fetch(`${API_BASE_URL}/newsrooms/${id}?populate[images][populate]=images`, {
      //   method: 'GET',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Accept': 'application/json',
      //   },
      // });
      
      // if (response.ok) {
      //   const data = await response.json();
      //   if (data.data) {
      //     return {
      //       data: data.data,
      //       success: true
      //     };
      //   }
      // }
      
      // If not found by Strapi ID, try to find by news_id in the collection
      console.log(`Direct ID ${id} not found, searching by news_id...`);
      const allPostsResponse = await this.getNewsroomPosts({
        'filters[news_id][$eq]': id,
        'pagination[limit]': 1
      });
      
      if (allPostsResponse.success && allPostsResponse.data.length > 0) {
        return {
          data: allPostsResponse.data[0],
          success: true
        };
      }
      
      // Try numeric search in all posts as fallback
      console.log(`News ID ${id} not found, searching in all posts...`);
      const allPosts = await this.getNewsroomPosts();
      if (allPosts.success && allPosts.data.length > 0) {
        // Find post by ID (either news_id or strapi_id)
        const foundPost = allPosts.data.find(post => 
          post.id === parseInt(id) || 
          post.id === id.toString() ||
          post.strapi_id === parseInt(id) ||
          post.strapi_id === id.toString() ||
          post.news_id === id
        );
        
        if (foundPost) {
          return {
            data: foundPost,
            success: true
          };
        }
        
        // If still not found and ID is numeric, try index-based lookup
        const numericId = parseInt(id, 10);
        if (!isNaN(numericId) && numericId > 0 && numericId <= allPosts.data.length) {
          console.warn(`Using index-based lookup for ID ${id}`);
          return {
            data: allPosts.data[numericId - 1],
            success: true
          };
        }
        
        // Return first available post as final fallback
        console.warn(`Post ID ${id} not found. Using first available post as fallback.`);
        return {
          data: allPosts.data[0],
          success: true
        };
      }
      
      throw new Error(`Post with ID ${id} not found`);
      
    } catch (error) {
      console.error('Error fetching newsroom post:', error);
      return {
        data: null,
        success: false,
        error: error.message || 'Failed to fetch newsroom post'
      };
    }
  }
};

// Helper function to format image URLs
export const formatImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // Use base Strapi URL (without /api) for images
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '');
  
  if (typeof imageData === 'object' && imageData.url) {
    return imageData.url.startsWith('http') ? imageData.url : `${baseUrl}${imageData.url}`;
  }
  
  return null;
};

// Helper function to convert Strapi blocks to Markdown with proper formatting
export const blocksToMarkdown = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return '';
  
  return blocks
    .map(block => {
      switch (block.type) {
        case 'paragraph':
          if (block.children) {
            const content = block.children
              .map(child => {
                let text = child.text || '';
                
                // Handle links first (before other formatting)
                if (child.type === 'link') {
                  const linkText = child.children ? child.children.map(c => c.text || '').join('') : text;
                  return `[${linkText}](${child.url})`;
                }
                
                // Handle formatting with markdown syntax
                if (child.bold) text = `**${text}**`;
                if (child.italic) text = `*${text}*`;
                if (child.underline) text = `<u>${text}</u>`; // HTML for underline as markdown doesn't support it
                if (child.strikethrough) text = `~~${text}~~`;
                
                // Handle inline code
                if (child.code) text = `` `${text}` ``;
                
                return text;
              })
              .join('');
            return content ? `${content}\n\n` : '';
          }
          return '';
          
        case 'heading':
          if (block.children) {
            const content = block.children
              .map(child => {
                let text = child.text || '';
                
                // Handle links first
                if (child.type === 'link') {
                  const linkText = child.children ? child.children.map(c => c.text || '').join('') : text;
                  return `[${linkText}](${child.url})`;
                }
                
                if (child.bold) text = `**${text}**`;
                if (child.italic) text = `*${text}*`;
                return text;
              })
              .join('');
            const level = block.level || 2;
            const hashes = '#'.repeat(level);
            return content ? `${hashes} ${content}\n\n` : '';
          }
          return '';
          
        case 'list':
          if (block.children) {
            const listItems = block.children
              .map((listItem, index) => {
                if (listItem.type === 'list-item' && listItem.children) {
                  const content = listItem.children
                    .map(child => {
                      let text = child.text || '';
                      
                      // Handle links first
                      if (child.type === 'link') {
                        const linkText = child.children ? child.children.map(c => c.text || '').join('') : text;
                        return `[${linkText}](${child.url})`;
                      }
                      
                      if (child.bold) text = `**${text}**`;
                      if (child.italic) text = `*${text}*`;
                      return text;
                    })
                    .join('');
                  
                  if (content) {
                    // Use markdown list syntax
                    if (block.format === 'ordered') {
                      return `${index + 1}. ${content}`;
                    } else {
                      return `- ${content}`;
                    }
                  }
                }
                return '';
              })
              .filter(item => item)
              .join('\n');
            
            return listItems ? `${listItems}\n\n` : '';
          }
          return '';
          
        case 'quote':
          if (block.children) {
            const content = block.children
              .map(child => child.text || '')
              .join('');
            return content ? `> ${content}\n\n` : '';
          }
          return '';
          
        case 'code':
          if (block.children) {
            const content = block.children
              .map(child => child.text || '')
              .join('');
            return content ? `\`\`\`\n${content}\n\`\`\`\n\n` : '';
          }
          return '';
          
        case 'link':
          // Handle standalone link blocks
          const linkText = block.children ? block.children.map(child => child.text || '').join('') : 'Link';
          return block.url ? `[${linkText}](${block.url})\n\n` : '';
          
        default:
          // Fallback for unknown block types
          if (block.children) {
            const content = block.children
              .map(child => child.text || '')
              .join('');
            return content ? `${content}\n\n` : '';
          }
          return '';
      }
    })
    .join('')
    .trim(); // Remove trailing whitespace
};

// Helper function to convert Strapi blocks to plain text (for short descriptions)
export const blocksToText = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return '';
  
  return blocks
    .map(block => {
      if (block.children) {
        return block.children.map(child => child.text || '').join('');
      }
      return '';
    })
    .filter(text => text.trim())
    .join(' ');
};

// Helper function to get all images from gallery images component
export const getAllImages = (imagesComponent) => {
  if (!imagesComponent || !Array.isArray(imagesComponent) || imagesComponent.length === 0) {
    return [];
  }
  
  const allImages = [];
  
  // Loop through all gallery items
  imagesComponent.forEach((galleryItem, galleryIndex) => {
    if (galleryItem && galleryItem.images) {
      // Handle both array and single image cases
      const images = Array.isArray(galleryItem.images) 
        ? galleryItem.images 
        : [galleryItem.images];
      
      images.forEach((image, imageIndex) => {
        const formattedUrl = formatImageUrl(image);
        if (formattedUrl) {
          allImages.push({
            id: image.id || `${galleryIndex}-${imageIndex}`,
            url: formattedUrl,
            name: image.name || `Image ${allImages.length + 1}`,
            width: image.width,
            height: image.height,
            alternativeText: image.alternativeText || image.name || `Image ${allImages.length + 1}`
          });
        }
      });
    }
  });
  
  return allImages;
};

// Helper function to get first image from gallery images component
export const getFirstImage = (imagesComponent) => {
  const allImages = getAllImages(imagesComponent);
  return allImages.length > 0 ? allImages[0].url : null;
};

// Helper function to calculate time ago
export const calculateTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays < 30) return `${diffInDays} days ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

// Helper function to format newsroom data for blog components
export const formatNewsroomData = (newsroomData) => {
  if (!newsroomData || !Array.isArray(newsroomData)) return [];

  return newsroomData.map((item, index) => {
    const attributes = item.attributes || item;
    
    // Extract all images from gallery component
    const allImages = getAllImages(attributes.images);
    const firstImage = allImages.length > 0 ? allImages[0].url : null;
    
    // Convert blocks to text for title and short description
    const title = blocksToText(attributes.title);
    const shortDescription = blocksToText(attributes.post_desc);
    
    // Convert blocks to Markdown for full post description (preserves formatting)
    const fullDescription = blocksToMarkdown(attributes.post_desc);
    
    return {
      id: attributes.news_id || item.id || `newsroom-${index}`, // Use news_id as primary ID
      news_id: attributes.news_id, // Keep original news_id field
      strapi_id: item.id, // Keep Strapi ID for API calls
      image: firstImage, // Keep this for backward compatibility
      images: allImages, // New: All images for carousel
      isVideo: false, // Newsroom posts are typically not videos
      videoUrl: null,
      tag: "News", // Default tag for newsroom posts
      title: title,
      desc: shortDescription, // Short text for cards/previews
      post_desc: fullDescription, // Full Markdown for detailed view
      content: fullDescription, // Same as post_desc for backward compatibility
      overlay: true,
      published_date: attributes.published_date, // Keep original published_date field
      // publishedAt: attributes.published_date || attributes.publishedAt || new Date().toISOString(),
      slug: `newsroom-${attributes.news_id || item.id || index}`,
      author: "BA Systems", // Default author
      hashtags: [], // No hashtags in newsroom schema
      timeAgo: calculateTimeAgo(attributes.published_date || attributes.publishedAt),
      // Additional newsroom specific fields
      team_id: attributes.team_id,
      venue: attributes.venue,
      user_pic: attributes.user_pic ? formatImageUrl({ url: attributes.user_pic }) : null,
      count_data: attributes.count_data,
      news_updated_by: attributes.news_updated_by,
      news_updated_at: attributes.news_updated_at,
    };
  });
};

// Helper function to format single newsroom post
export const formatSingleNewsroomPost = (newsroomItem) => {
  if (!newsroomItem) return null;
  
  const attributes = newsroomItem.attributes || newsroomItem;
  
  // Extract all images from gallery component
  const allImages = getAllImages(attributes.images);
  const firstImage = allImages.length > 0 ? allImages[0].url : null;
  
  // Convert blocks to text for title and short description
  const title = blocksToText(attributes.title);
  const shortDescription = blocksToText(attributes.post_desc);
  
  // Convert blocks to Markdown for full post description (preserves formatting)
  const fullDescription = blocksToMarkdown(attributes.post_desc);
  
  return {
    id: attributes.news_id || newsroomItem.id, // Use news_id as primary ID
    news_id: attributes.news_id, // Keep original news_id field
    strapi_id: newsroomItem.id, // Keep Strapi ID for API calls
    image: firstImage, // Keep this for backward compatibility
    images: allImages, // New: All images for carousel
    isVideo: false,
    videoUrl: null,
    tag: "News",
    title: title,
    desc: shortDescription, // Short text for cards/previews
    post_desc: fullDescription, // Full Markdown for detailed view
    content: fullDescription, // Same as post_desc for backward compatibility
    overlay: true,
    published_date: attributes.published_date, // Keep original published_date field
    publishedAt: attributes.published_date || attributes.publishedAt || new Date().toISOString(),
    slug: `newsroom-${attributes.news_id || newsroomItem.id}`,
    author: "BA Systems",
    hashtags: [],
    timeAgo: calculateTimeAgo(attributes.published_date || attributes.publishedAt),
    // Additional newsroom specific fields
    team_id: attributes.team_id,
    venue: attributes.venue,
    user_pic: attributes.user_pic ? formatImageUrl({ url: attributes.user_pic }) : null,
    count_data: attributes.count_data,
    news_updated_by: attributes.news_updated_by,
    news_updated_at: attributes.news_updated_at,
  };
};

export default newsroomService;